/**
 * @module features/clients/components/ClientStatsCards
 * @description KPI metric cards matching the modern system design of Device & User administration.
 * Features rounded-2xl cards, dark squircle icon tiles, Inter bold typography, semantic status pills,
 * and an interactive fleet capacity utilization bar.
 */
import * as React from "react";
import { Building2, CheckCircle2, AlertTriangle, Users2, Gauge } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ClientFleetStats } from "../types";

export interface ClientStatsCardsProps {
  stats?: ClientFleetStats;
  isLoading?: boolean;
}

const CARD_BASE =
  "flex items-center justify-between p-3.5 sm:p-4 h-[76px] rounded-2xl border border-border/70 dark:border-zinc-800/80 bg-card dark:bg-zinc-950 shadow-xs hover:shadow-sm transition-all duration-150";

export function ClientStatsCards({ stats, isLoading }: ClientStatsCardsProps) {
  if (isLoading || !stats) {
    return (
      <div className="space-y-3" aria-busy="true">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={CARD_BASE}>
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-xl" />
                <div className="space-y-1.5">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-6 w-12" />
                </div>
              </div>
              <Skeleton className="h-5 w-12 rounded-full hidden sm:block" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const cards = [
    {
      label: "Total Orgs",
      value: stats.total_clients,
      icon: Building2,
      badgeText: "Multi-Tenant",
      badgeStyle:
        "text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-850 border border-zinc-200 dark:border-zinc-800",
    },
    {
      label: "Active Portals",
      value: stats.active_clients,
      icon: CheckCircle2,
      badgeText: "Operational",
      badgeStyle:
        "text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800/50",
    },
    {
      label: "Suspended",
      value: stats.suspended_clients,
      icon: AlertTriangle,
      badgeText: stats.suspended_clients > 0 ? "Attention" : "Zero Paused",
      badgeStyle:
        stats.suspended_clients > 0
          ? "text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800/50"
          : "text-zinc-600 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-850 border border-zinc-200 dark:border-zinc-800",
    },
    {
      label: "Linked Client Users",
      value: stats.total_linked_users,
      icon: Users2,
      badgeText: `${stats.avg_quota_utilization_pct}% Quota`,
      badgeStyle:
        "text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-800/50",
    },
  ];

  return (
    <div className="space-y-3">
      {/* 4 Primary Operational Metric Cards */}
      <section
        aria-label="Client fleet operational summary"
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
      >
        {cards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <div key={idx} className={CARD_BASE}>
              <div className="flex items-center gap-3 sm:gap-3.5 min-w-0">
                {/* Dark squircle icon tile from reference design */}
                <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-xl bg-zinc-900 dark:bg-zinc-800 text-white dark:text-zinc-100 shadow-xs shrink-0 ring-1 ring-black/5 dark:ring-white/10">
                  <Icon className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" aria-hidden="true" />
                </div>

                <div className="min-w-0">
                  <p className="text-[11px] sm:text-xs font-medium text-muted-foreground truncate">
                    {card.label}
                  </p>
                  <p className="text-lg sm:text-2xl font-bold tracking-tight text-foreground truncate">
                    {card.value}
                  </p>
                </div>
              </div>

              {/* Semantic status pill */}
              <span
                className={`hidden sm:inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0 ${card.badgeStyle}`}
              >
                {card.badgeText}
              </span>
            </div>
          );
        })}
      </section>

      {/* Fleet Utilization Progress Bar Banner */}
      <section
        aria-label="Fleet seat quota utilization summary"
        className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-3 sm:py-2.5 sm:px-4 rounded-2xl border border-border/70 dark:border-zinc-800/80 bg-card dark:bg-zinc-950 shadow-xs text-xs"
      >
        <div className="flex items-center gap-2 text-muted-foreground">
          <Gauge className="h-4 w-4 text-foreground/70 shrink-0" />
          <span className="font-semibold text-foreground">Users Seat Allocation:</span>
          <span className="font-mono text-muted-foreground">
            {stats.total_linked_users} total active client seats assigned
          </span>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-32 sm:w-44 h-2 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full transition-all duration-500 rounded-full ${stats.avg_quota_utilization_pct > 90
                ? "bg-rose-500"
                : stats.avg_quota_utilization_pct > 75
                  ? "bg-amber-500"
                  : "bg-emerald-500"
                }`}
              style={{ width: `${Math.min(100, Math.max(0, stats.avg_quota_utilization_pct))}%` }}
            />
          </div>
          <span className="font-mono font-semibold text-foreground whitespace-nowrap">
            {stats.avg_quota_utilization_pct}% avg utilization
          </span>
        </div>
      </section>
    </div>
  );
}
